import * as React from 'react';
import {ColumnCode as ColumnCodeValues} from 'priority-web-sdk/enums';
import {ColumnCode} from 'priority-web-sdk';

interface RenderProps {
    render(props: InjectedNumberProps): JSX.Element;
}

interface NumberProps {
    value?: string | number;
    prefix?: string;
    maxLength?: number;
    decimal?: number;
    code?: ColumnCode;
    positive?: boolean;
    onUpdate?(value: string): void;
    onChange(value: string): void;
    decimalSign: string;
    thousandSign: string;
    disabled?: boolean;
}

type Props = NumberProps & RenderProps;

interface State {
    value: string;
    formated: string;
    inputLength: number;
}

export interface InjectedNumberProps extends State {
    placeholder: string;
    handleChange(value: string): void;
    handleKeyUp(event): void;
    handleEndEditing(number: string): void;
}

function getPrecision({code, decimal}: NumberProps): number {
    return code === ColumnCodeValues.Real ? decimal : 0;
}

function display(value: string, props: NumberProps): string {
    const {prefix, thousandSign, decimalSign} = props;

    if (value === '' || value === '-' || value === 'NULL') {
        return value;
    }

    if (value.indexOf('?') > -1) {
        return value;
    }

    if (value.indexOf('*') > -1) {
        return value;
    }
    if (value.indexOf('_') > -1) {
        return value;
    }

    const locale = decimalSign === ',' ? 'nl' : 'en';

    if (value.indexOf(decimalSign) > -1) {
        const [number, decimal] = value.split(decimalSign);

        return (
            prefix +
            Number(number.split(thousandSign).join('')).toLocaleString(locale) +
            decimalSign +
            decimal
        );
    }

    return prefix + Number(value.split(thousandSign).join('')).toLocaleString(locale);
}

function calcInputLength(number: string, {maxLength, prefix, ...props}: NumberProps): number {
    const precision = getPrecision(props as NumberProps);
    const length = maxLength + prefix.length;

    if (number.indexOf(props.decimalSign) > -1) {
        return number.indexOf(props.decimalSign) + 1 + precision;
    }

    return length - precision;
}

function isValid(value: string, props): boolean {
    const {maxLength, positive, code, decimalSign, thousandSign} = props;

    const number = value.split(thousandSign).join('');
    /**
     * check if type/delete NULL
     */
    if (value === 'N' || value === 'NUL') {
        return true;
    }
    // check valid characters
    if (isNaN(Number(number.replace(/[?|*|,|_|-]/g, '')))) {
        return false;
    }

    // check '-' (minus) validation
    if (
        number.indexOf('-') > 0 ||
        (number.indexOf('-') === 0 && (positive || code === ColumnCodeValues.Unasigned))
    ) {
        return false;
    }

    // check decimal precision validation
    if (props.decimal === 0 && number.indexOf(decimalSign) > -1) {
        return false;
    }

    // check length validation
    if (maxLength) {
        const parts = number.split(decimalSign);
        const beforeDecimalLength = parts[0].length;
        const afterDecimalLength = parts[1] ? parts[1].length : 0;
        const pointLength = afterDecimalLength > 0 ? 1 : 0;

        return (
            beforeDecimalLength + afterDecimalLength + pointLength <= maxLength &&
            afterDecimalLength <= props.decimal
        );
    }

    return true;
}

export class NumberControl extends React.Component<Props, State> {
    static defaultProps = {
        value: '',
        prefix: '',
        decimal: 0,
        positive: false,
        decimalSign: '.',
        thousandSign: ','
    };

    constructor(props) {
        super(props);

        const {value} = props;

        this.state = {
            value,
            formated: display(value, props),
            inputLength: calcInputLength(value, props)
        };

        this.handleKeyUp = this.handleKeyUp.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleEndEditing = this.handleEndEditing.bind(this);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const {value} = this.state;

        if (value !== nextProps.value) {
            const nextNumber = nextProps.value || '';

            this.setState({
                value: nextProps.value,
                formated: display(nextNumber, nextProps),
                inputLength: calcInputLength(nextNumber, nextProps)
            });
        }
    }

    handleChange(value: string) {
        const {prefix, onChange} = this.props;
        if (value.length < prefix.length) return;

        const number = value.substring(prefix.length);

        if (isValid(number, this.props)) {
            let newValue = value;
            const oldValue = this.state.value;
            /**
             * check if type/delete NULL
             */

            if (newValue === 'N') {
                newValue = 'NULL';
            } else if (oldValue === 'NaN' && newValue === 'Na') {
                newValue = '';
            } else if (oldValue === 'NULL' && newValue === 'NUL') {
                newValue = '';
            }

            this.setState({
                value: newValue,
                formated: display(newValue, this.props),
                inputLength: calcInputLength(newValue, this.props)
            });
            onChange && onChange(newValue);
        }
    }

    handleKeyUp({nativeEvent}) {
        if (nativeEvent.code === 'NumpadDecimal') {
            if ([',', '.'].includes(this.state.formated.slice(-1))) {
                this.setState({
                    formated: `${this.state.formated.slice(0, -1)}${this.props.decimalSign}`
                });
            }
        }
    }

    handleEndEditing() {
        const {onUpdate} = this.props;
        const {value} = this.state;
        onUpdate(value);
    }

    render() {
        const {render: Component, decimalSign} = this.props;
        const precision = getPrecision(this.props);
        const placeholder = precision ? `0${decimalSign}${'0'.repeat(precision)}` : '0';
        return (
            <Component
                {...this.props}
                {...this.state}
                placeholder={placeholder}
                handleChange={this.handleChange}
                handleKeyUp={this.handleKeyUp}
                handleEndEditing={this.handleEndEditing}
            />
        );
    }
}

export const withNumber = <P extends InjectedNumberProps>(Component: React.ComponentType<P>) => {
    return props => {
        return <NumberControl {...props} render={p => <Component {...(p as P)} />} />;
    };
};
